import reactGa4Module from 'react-ga4'
import { getCookieConsent } from './lib/cookie-consent/cookie-consent-store'
import { getAppConfig } from './lib/env.dynamic.client'

/**
 * Rolldown/Vite CJS interop can surface `react-ga4` as a nested `{ default: ... }` chain
 * instead of the GA4 singleton — unwrap until we find the object that exposes `initialize`.
 */
type ReactGa4Client = {
  initialize: (measurementId: string | unknown[], options?: Record<string, unknown>) => void
  send: (fieldObject: Record<string, unknown>) => void
  event: (optionsOrName: Record<string, unknown> | string, params?: Record<string, unknown>) => void
}

function getReactGa4Client(): ReactGa4Client | null {
  let current: unknown = reactGa4Module
  for (let depth = 0; depth < 4; depth++) {
    if (
      current !== null &&
      typeof current === 'object' &&
      'initialize' in current &&
      typeof (current as { initialize: unknown }).initialize === 'function'
    ) {
      return current as ReactGa4Client
    }
    if (current !== null && typeof current === 'object' && 'default' in current) {
      current = (current as { default: unknown }).default
      continue
    }
    break
  }
  return null
}

let analyticsInitialized = false

function hasAnalyticsConsent(): boolean {
  return getCookieConsent().analytics
}

export const trackingScriptsNeedReloadOnConsentChange = (next: { analytics: boolean }): boolean => {
  return getCookieConsent().analytics !== next.analytics
}

export const initGA = () => {
  if (!hasAnalyticsConsent()) {
    return
  }
  if (analyticsInitialized) {
    return
  }
  if (typeof window === 'undefined') {
    return
  }

  const appConfig = getAppConfig()
  if (!appConfig.GOOGLE_ANALYTICS) {
    console.warn('Google Analytics is not configured')
    return
  }
  const reactGa = getReactGa4Client()
  if (!reactGa) {
    console.warn('Google Analytics client could not be loaded (unexpected module shape)')
    return
  }
  try {
    reactGa.initialize(appConfig.GOOGLE_ANALYTICS)
    analyticsInitialized = true
  } catch (error) {
    console.warn('Google Analytics failed to initialize', error)
  }
}

export const logPageView = () => {
  if (!hasAnalyticsConsent()) {
    return
  }
  if (!analyticsInitialized) {
    return
  }
  const reactGa = getReactGa4Client()
  if (!reactGa) {
    return
  }
  try {
    reactGa.send({ hitType: 'pageview', page: window.location.pathname + window.location.search })
  } catch (error) {
    console.warn('Analytics logPageView failed', error)
  }
}

export const logEvent = (category: string, action: string, label?: string) => {
  if (!hasAnalyticsConsent()) {
    return
  }
  if (!analyticsInitialized) {
    return
  }
  const reactGa = getReactGa4Client()
  if (!reactGa) {
    return
  }
  try {
    reactGa.event({ category, action, label })
  } catch (error) {
    console.warn('Analytics logEvent failed', error)
  }
}
